import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Check, Copy, Download, Eye, EyeOff, KeyRound, LifeBuoy, Lock, MonitorSmartphone, RefreshCw, ShieldCheck, Smartphone } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";

const OTP_SLOT = "h-12 w-11 border-lux-border bg-lux-surface2 text-lg";

/* ---------- shared: password + second factor fields ---------- */
function ReauthFields({ password, setPassword, code, setCode, idPrefix }) {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-4">
      <div>
        <label htmlFor={`${idPrefix}-password`} className="lux-label">Current password</label>
        <div className="relative mt-2">
          <input
            id={`${idPrefix}-password`}
            type={show ? "text" : "password"}
            className="lux-input pr-12"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            data-testid={`${idPrefix}-current-password-input`}
          />
          <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Hide password" : "Show password"}
            className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-lux-muted hover:text-lux-text" data-testid={`${idPrefix}-password-toggle`}>
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <div>
        <label htmlFor={`${idPrefix}-code`} className="lux-label">Authenticator code <span className="normal-case tracking-normal text-lux-muted">(or a recovery code)</span></label>
        <input
          id={`${idPrefix}-code`}
          className="lux-input mt-2 font-mono tracking-[0.15em]"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoComplete="one-time-code"
          inputMode="text"
          placeholder="123456 or XXXXX-XXXXX"
          required
          data-testid={`${idPrefix}-code-input`}
        />
      </div>
    </div>
  );
}

/* ---------- recovery codes display (shown once) ---------- */
function RecoveryCodes({ codes, testId = "recovery-codes" }) {
  const [copied, setCopied] = useState(false);
  const text = codes.join("\n");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Recovery codes copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error("Copy not supported on this device");
    }
  };
  const download = () => {
    const blob = new Blob([`LUX recovery codes (each works once)\n\n${text}\n`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "lux-recovery-codes.txt";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };
  return (
    <div data-testid={testId}>
      <ul className="grid grid-cols-2 gap-2 rounded-[10px] border border-lux-border bg-lux-surface2 p-4 font-mono text-[15px] tracking-[0.12em] text-lux-silverBright">
        {codes.map((c) => (
          <li key={c} data-testid="recovery-code">{c}</li>
        ))}
      </ul>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={copy} className="lux-btn lux-btn-secondary lux-btn-sm" data-testid={`${testId}-copy`}>
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copy
        </button>
        <button type="button" onClick={download} className="lux-btn lux-btn-secondary lux-btn-sm" data-testid={`${testId}-download`}>
          <Download className="h-4 w-4" /> Download .txt
        </button>
      </div>
      <p className="mt-3 text-xs text-lux-error">These codes are shown once. Each one signs you in a single time if you lose your phone.</p>
    </div>
  );
}

/* ---------- re-configure authenticator: 3-step dialog ---------- */
function ResetTwoFactorDialog({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState(null);
  const [newCode, setNewCode] = useState("");
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setStep(1); setPassword(""); setCode(""); setSetup(null); setNewCode(""); setCodes([]); setError(""); setBusy(false);
    }
  }, [open]);

  const start = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/advertiser/security/2fa/reset/start", { current_password: password, code: code.trim() });
      setSetup(data);
      setStep(2);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e) => {
    e?.preventDefault?.();
    if (newCode.length !== 6) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/advertiser/security/2fa/reset/confirm", { code: newCode });
      setCodes(data.recovery_codes || []);
      setStep(3);
      onDone?.();
    } catch (err) {
      setError(errMsg(err));
      setNewCode("");
    } finally {
      setBusy(false);
    }
  };

  const close = async (v) => {
    if (busy) return;
    if (!v && step === 2) {
      try { await api.post("/advertiser/security/2fa/reset/cancel"); } catch (_) {}
    }
    onOpenChange(v);
  };

  const copySecret = async () => {
    try { await navigator.clipboard.writeText(setup.secret); toast.success("Secret copied"); } catch (_) { toast.error("Copy not supported on this device"); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="border-lux-border bg-lux-surface text-lux-text sm:max-w-md" data-testid="reset-2fa-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl font-medium">
            <Smartphone className="h-5 w-5 text-lux-silver" aria-hidden="true" /> New authenticator app
          </DialogTitle>
          <DialogDescription className="text-lux-muted">
            {step === 1 && "Step 1 of 3 - confirm it's you. Your current app keeps working until the new one is confirmed."}
            {step === 2 && "Step 2 of 3 - scan the QR with the new phone or app, then enter the code it shows."}
            {step === 3 && "Step 3 of 3 - save your new recovery codes. Your old codes and old app no longer work."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <form onSubmit={start} className="space-y-5">
            <ReauthFields password={password} setPassword={setPassword} code={code} setCode={setCode} idPrefix="reset-2fa" />
            {error && <p className="text-sm text-lux-error" role="alert" data-testid="reset-2fa-error">{error}</p>}
            <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => close(false)} className="lux-btn lux-btn-ghost" data-testid="reset-2fa-cancel-button">Cancel</button>
              <button type="submit" disabled={busy || !password || code.trim().length < 6} className="lux-btn lux-btn-primary" data-testid="reset-2fa-start-button">
                {busy ? "Checking..." : "Continue"}
              </button>
            </DialogFooter>
          </form>
        )}

        {step === 2 && setup && (
          <form onSubmit={confirm} className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-[150px_1fr]">
              <div className="flex items-center justify-center rounded-[12px] border border-lux-border bg-white p-3" data-testid="reset-2fa-qr">
                <QRCodeCanvas value={setup.otpauth_uri} size={130} level="M" includeMargin={false} />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-lux-muted">Can't scan? Type this secret into the app:</p>
                <div className="mt-2 flex items-center gap-2">
                  <code className="min-w-0 flex-1 break-all rounded-[8px] border border-lux-border bg-lux-surface2 px-3 py-2 font-mono text-[12px] text-lux-silverBright" data-testid="reset-2fa-secret">{setup.secret}</code>
                  <button type="button" onClick={copySecret} className="lux-btn lux-btn-secondary lux-btn-sm" aria-label="Copy secret" data-testid="reset-2fa-copy-secret"><Copy className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
            <div>
              <label className="lux-label">Code from the new app</label>
              <div className="mt-2">
                <InputOTP maxLength={6} value={newCode} onChange={setNewCode} containerClassName="gap-2" data-testid="reset-2fa-new-code-input">
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className={OTP_SLOT} />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>
            {error && <p className="text-sm text-lux-error" role="alert" data-testid="reset-2fa-error">{error}</p>}
            <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => close(false)} className="lux-btn lux-btn-ghost" data-testid="reset-2fa-cancel-button">Cancel</button>
              <button type="submit" disabled={busy || newCode.length !== 6} className="lux-btn lux-btn-primary" data-testid="reset-2fa-confirm-button">
                {busy ? "Activating..." : "Activate new app"}
              </button>
            </DialogFooter>
          </form>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <RecoveryCodes codes={codes} testId="reset-2fa-recovery-codes" />
            <DialogFooter>
              <button type="button" onClick={() => onOpenChange(false)} className="lux-btn lux-btn-primary w-full sm:w-auto" data-testid="reset-2fa-done-button">
                <Check className="h-4 w-4" /> I've saved my codes
              </button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- regenerate recovery codes dialog ---------- */
function RecoveryCodesDialog({ open, onOpenChange, onDone }) {
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { if (!open) { setPassword(""); setCode(""); setCodes([]); setError(""); setBusy(false); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/advertiser/security/recovery-codes", { current_password: password, code: code.trim() });
      setCodes(data.recovery_codes || []);
      onDone?.();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="border-lux-border bg-lux-surface text-lux-text sm:max-w-md" data-testid="recovery-codes-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl font-medium"><LifeBuoy className="h-5 w-5 text-lux-silver" aria-hidden="true" /> Recovery codes</DialogTitle>
          <DialogDescription className="text-lux-muted">
            {codes.length ? "Save these somewhere safe. Any older codes have stopped working." : "Eight single-use codes that sign you in if you lose your phone. Generating new ones replaces the old set."}
          </DialogDescription>
        </DialogHeader>
        {codes.length ? (
          <div className="space-y-5">
            <RecoveryCodes codes={codes} testId="regen-recovery-codes" />
            <DialogFooter>
              <button type="button" onClick={() => onOpenChange(false)} className="lux-btn lux-btn-primary w-full sm:w-auto" data-testid="recovery-codes-done-button"><Check className="h-4 w-4" /> I've saved my codes</button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <ReauthFields password={password} setPassword={setPassword} code={code} setCode={setCode} idPrefix="recovery" />
            {error && <p className="text-sm text-lux-error" role="alert" data-testid="recovery-codes-error">{error}</p>}
            <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => onOpenChange(false)} className="lux-btn lux-btn-ghost" data-testid="recovery-codes-cancel-button">Cancel</button>
              <button type="submit" disabled={busy || !password || code.trim().length < 6} className="lux-btn lux-btn-primary" data-testid="recovery-codes-submit-button">
                {busy ? "Generating..." : "Generate new codes"}
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- change password card ---------- */
function PasswordCard({ locked, changedAt, onChanged }) {
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const mismatch = confirm && next !== confirm;
  const ready = password && code.trim().length >= 6 && next.length >= 10 && next === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/advertiser/security/password", { current_password: password, code: code.trim(), new_password: next });
      toast.success("Password changed", { description: data.other_sessions_revoked ? `${data.other_sessions_revoked} other device(s) were signed out.` : "You stay signed in on this device." });
      setPassword(""); setCode(""); setNext(""); setConfirm("");
      onChanged?.();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="lux-card p-5 sm:p-6" data-testid="security-password-card">
      <p className="lux-label flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" aria-hidden="true" /> Password</p>
      <h2 className="mt-2 font-serif text-2xl text-lux-text">Change your password</h2>
      <p className="mt-2 text-sm text-lux-muted">At least 10 characters with letters and numbers. Changing it signs out every other device.{changedAt ? ` Last changed ${fmtDate(changedAt)}.` : ""}</p>
      <form onSubmit={submit} className="mt-5 grid gap-5 lg:grid-cols-2">
        <ReauthFields password={password} setPassword={setPassword} code={code} setCode={setCode} idPrefix="pw" />
        <div className="space-y-4">
          <div>
            <label htmlFor="pw-new" className="lux-label">New password</label>
            <div className="relative mt-2">
              <input id="pw-new" type={showNext ? "text" : "password"} className="lux-input pr-12" value={next} onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password" minLength={10} required placeholder="At least 10 characters, mix letters & numbers" data-testid="pw-new-password-input" />
              <button type="button" onClick={() => setShowNext((v) => !v)} aria-label={showNext ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-lux-muted hover:text-lux-text" data-testid="pw-new-password-toggle">
                {showNext ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <label htmlFor="pw-confirm" className="lux-label">Repeat new password</label>
            <input id="pw-confirm" type={showNext ? "text" : "password"} className={`lux-input mt-2 ${mismatch ? "border-lux-error" : ""}`} value={confirm} onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password" required aria-invalid={!!mismatch} data-testid="pw-confirm-password-input" />
            {mismatch && <p className="mt-1.5 text-xs text-lux-error" data-testid="pw-mismatch">Passwords don't match</p>}
          </div>
        </div>
        <div className="lg:col-span-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {error ? <p className="text-sm text-lux-error" role="alert" data-testid="pw-error">{error}</p> : <span />}
          <button type="submit" disabled={!ready || busy || locked} className="lux-btn lux-btn-primary sm:w-56" data-testid="pw-submit-button">
            <KeyRound className="h-4 w-4" aria-hidden="true" /> {busy ? "Saving..." : "Change password"}
          </button>
        </div>
      </form>
    </section>
  );
}

export default function SecurityPage() {
  useSeo({ title: "Sign-in & security | LUX", ...PRIVATE_SEO });
  const client = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["advertiser", "security"],
    queryFn: async () => (await api.get("/advertiser/security")).data,
  });
  const [resetOpen, setResetOpen] = useState(false);
  const [codesOpen, setCodesOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const refresh = () => Promise.all([refetch(), client.invalidateQueries({ queryKey: ["advertiser", "me"] })]);

  const revokeOthers = async () => {
    setRevoking(true);
    try {
      const { data: r } = await api.post("/advertiser/security/sessions/revoke-others");
      toast.success(r.revoked ? `Signed out of ${r.revoked} other device${r.revoked === 1 ? "" : "s"}` : "No other devices were signed in");
      refetch();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setRevoking(false);
    }
  };

  if (isLoading && !data) {
    return <div className="text-sm text-lux-muted" data-testid="security-loading">Loading security settings...</div>;
  }
  const locked = !!data?.locked;
  const recovery = data?.recovery || {};

  return (
    <div className="space-y-8" data-testid="security-page">
      <header>
        <p className="lux-label">Sign-in & security</p>
        <h1 className="mt-2 font-serif text-4xl text-lux-text sm:text-5xl">Keep your account yours</h1>
        <p className="mt-3 max-w-2xl text-sm text-lux-muted">
          Every change here asks for your current password and a code from your authenticator (or a recovery code), so an open session alone can't take over your account.
        </p>
        {locked && (
          <p className="mt-3 rounded-[10px] border border-lux-error/35 bg-lux-error/5 px-4 py-3 text-sm text-lux-text/85" data-testid="security-locked-note">
            Security settings are locked while your account is scheduled for deletion. Cancel the deletion in Account & data to change them.
          </p>
        )}
      </header>

      <PasswordCard locked={locked} changedAt={data?.password_changed_at} onChanged={refresh} />

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="lux-card p-5 sm:p-6" data-testid="security-2fa-card">
          <p className="lux-label flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5" aria-hidden="true" /> Authenticator app</p>
          <h2 className="mt-2 font-serif text-2xl text-lux-text">Two-factor authentication</h2>
          <p className="mt-2 text-sm text-lux-muted">
            2FA is always on for LUX accounts. New phone, or switching apps? Set up a new authenticator here - the old one stops working once the new one is confirmed.
            {data?.two_factor?.reconfigured_at ? ` Last set up ${fmtDate(data.two_factor.reconfigured_at)}.` : ""}
          </p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-lux-success/35 bg-lux-success/10 px-3 py-1 text-xs font-medium text-lux-success" data-testid="security-2fa-status">
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> Enabled
          </div>
          <div className="mt-5">
            <button onClick={() => setResetOpen(true)} disabled={locked} className="lux-btn lux-btn-secondary w-full sm:w-auto" data-testid="security-reset-2fa-button">
              <RefreshCw className="h-4 w-4" aria-hidden="true" /> Set up a new authenticator
            </button>
          </div>
        </section>

        <section className="lux-card p-5 sm:p-6" data-testid="security-recovery-card">
          <p className="lux-label flex items-center gap-1.5"><LifeBuoy className="h-3.5 w-3.5" aria-hidden="true" /> Lost your phone?</p>
          <h2 className="mt-2 font-serif text-2xl text-lux-text">Recovery codes</h2>
          <p className="mt-2 text-sm text-lux-muted">Eight single-use codes that replace the authenticator code when you sign in or change these settings. Store them somewhere private.</p>
          <p className="mt-4 font-serif text-3xl text-lux-text" data-testid="security-recovery-remaining">
            {recovery.remaining ?? 0} <span className="font-sans text-sm text-lux-muted">unused code{recovery.remaining === 1 ? "" : "s"}</span>
          </p>
          <p className="mt-1 text-xs text-lux-muted">
            {recovery.generated_at ? `Issued ${fmtDate(recovery.generated_at)}` : "No codes yet - generate a set now so you're never locked out."}
            {recovery.last_used_at ? ` - last used ${fmtDate(recovery.last_used_at)}` : ""}
          </p>
          <div className="mt-5">
            <button onClick={() => setCodesOpen(true)} disabled={locked} className="lux-btn lux-btn-secondary w-full sm:w-auto" data-testid="security-recovery-codes-button">
              <KeyRound className="h-4 w-4" aria-hidden="true" /> {recovery.remaining ? "Generate new codes" : "Generate recovery codes"}
            </button>
          </div>
        </section>
      </div>

      <section className="lux-card flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6" data-testid="security-sessions-card">
        <div>
          <p className="lux-label flex items-center gap-1.5"><MonitorSmartphone className="h-3.5 w-3.5" aria-hidden="true" /> Devices</p>
          <h2 className="mt-2 font-serif text-2xl text-lux-text">Signed-in devices</h2>
          <p className="mt-2 text-sm text-lux-muted">
            <span data-testid="security-active-sessions">{data?.active_sessions ?? 1}</span> active session{data?.active_sessions === 1 ? "" : "s"}, including this one.
            {data?.last_login_at ? ` Last sign-in ${fmtDate(data.last_login_at, true)}.` : ""}
          </p>
        </div>
        <button onClick={revokeOthers} disabled={revoking || !data?.other_sessions} className="lux-btn lux-btn-secondary sm:w-64" data-testid="security-revoke-others-button">
          {revoking ? "Signing out..." : data?.other_sessions ? `Sign out ${data.other_sessions} other device${data.other_sessions === 1 ? "" : "s"}` : "No other devices"}
        </button>
      </section>

      <ResetTwoFactorDialog open={resetOpen} onOpenChange={setResetOpen} onDone={refresh} />
      <RecoveryCodesDialog open={codesOpen} onOpenChange={setCodesOpen} onDone={refresh} />
    </div>
  );
}
